import { snakeCase, intersection } from 'lodash'

import { getSelectedKubernetesTargets } from 'components/attacks/create/targets/kubernetes/kubernetes-target-utils'
import {
  convertParamsToSets,
  getTotalSelectedClients,
  isTargetParamsEmpty,
  getTargetParamsKey
} from 'components/attacks/create/targets/target-utils'
import {
  getGraphNodesByType,
  scenarioGraphTypes,
  graphNodeToStepAttack
} from 'components/scenarios/utils/graph'
import {
  isActive,
  isComplete
} from 'components/scenarios/utils/graph-node-lifecycle'
import { naturalSortBy, unique } from 'utils/list'
import { cleanObjKeysAndValues } from 'utils/obj'

const countableNodeTypes = [
  scenarioGraphTypes.InfraAttack,
  scenarioGraphTypes.SynchronousStatusCheck
]

export function getScheduleMaxRuns(schedule) {
  return schedule.trigger.maxRuns && Number(schedule.trigger.maxRuns)
}

export function getScenarioStepCount(scenario) {
  const countableNodes = getGraphNodesByType(
    scenario.graph,
    ...countableNodeTypes
  )
  return countableNodes.length
}

export function getCurrentStepForRun(run) {
  if (run) {
    const steps = getGraphNodesByType(run.graph, ...countableNodeTypes)
    const completedNodes = steps
      .map(
        (step) =>
          isActive(step.state?.lifecycle) || isComplete(step.state?.lifecycle)
      )
      .filter((successful) => successful === true)

    return completedNodes.length
  }
  return 0
}

export function getFirstAttackAsStep(scenario) {
  const attacks = getGraphNodesByType(
    scenario?.graph,
    scenarioGraphTypes.InfraAttack
  )
  if (!attacks.length) return
  return graphNodeToStepAttack(attacks[0])
}

export function getAttackFromStep(step, attackIndex = 0) {
  return step?.attacks[attackIndex]
}

export function getTaskFromSummary(summary) {
  if (!summary) return

  return Object.values(summary?.taskSummaries)[0]
}

export function scenarioEditedSinceRun(scenario, run) {
  if (!run) {
    return true
  }
  return run.scenario_edit_counter_at_run_time < scenario.edit_counter
}

export function getTargetTypeAndCommand(scenario) {
  const firstAttack = getFirstAttackAsStep(scenario)
  if (!firstAttack) return {}
  const command = firstAttack.impactDefinition.commandType
  const targetType = firstAttack.targetDefinition.targetType
  return { targetType, command }
}

export function getScenarioCommands(scenario) {
  return unique(
    getGraphNodesByType(scenario.graph, scenarioGraphTypes.InfraAttack).map(
      (node) => node.impact_definition.infra_command_args.cli_args[0]
    )
  )
}

export function getScenarioTargetTypes(scenario) {
  return unique(
    getGraphNodesByType(scenario.graph, scenarioGraphTypes.InfraAttack).map(
      (node) => node.target_definition.target_type
    )
  )
}

export function getBlastRadiusForStep(step) {
  const attack = getAttackFromStep(step)
  const isHost = 'Host' === attack?.targetDefinition?.targetType ?? 'Host'
  const blastRadiusLabel = attack.targetDefinition.strategy.percentage
    ? `${attack.targetDefinition.strategy.percentage}%`
    : attack.targetDefinition.strategy.count

  const blastRadiusValue = targetOverviewSubtitle(
    isHost,
    attack.targetDefinition.strategy
  )

  return {
    blastRadiusLabel,
    blastRadiusValue
  }
}

export function getBlastRadiusForNode(node) {
  const attack = node
  const isHost = 'Host' === attack?.target_definition?.target_type ?? 'Host'
  const strategy = attack.target_definition.strategy
  const blastRadiusLabel = strategy.percentage
    ? `${strategy.percentage}%`
    : strategy.count

  const blastRadiusValue = targetOverviewSubtitle(isHost, strategy)

  return {
    blastRadiusLabel,
    blastRadiusValue
  }
}

export function targetOverviewSubtitle(isHost, strategy) {
  const prefix = strategy.percentage ? 'of selected' : ''
  const suffix = strategy.percentage ? '' : 'selected'
  const type = isHost ? 'hosts' : 'containers'
  return `${prefix} ${type} ${suffix}`
}

export function targetCreationSubtitle(
  isHost,
  activeClients,
  selectedTargetParams,
  targetAll
) {
  const selectedTargetParamsSets = convertParamsToSets(selectedTargetParams)

  return `of ${getTotalSelectedClients(
    activeClients,
    'Random',
    isHost ? 'Hosts' : 'Containers',
    [],
    selectedTargetParamsSets,
    targetAll
  )}`
}

export const sortScenarioListDesc = (scenarios) =>
  naturalSortBy(
    scenarios,
    ({ last_run_at, created_at }) => last_run_at || created_at,
    'desc'
  )
export const filterScenariosByCommands = (scenarios, search = []) => {
  if (search.length === 0) return scenarios

  return scenarios.filter((scenario) => {
    const command =
      Object.values(scenario.graph?.nodes ?? {}).map((node) =>
        node?.impact_definition?.infra_command_type?.toLowerCase()
      ) ?? []

    const matchesCommand = intersection(command, search).length > 0
    if (matchesCommand) {
      return true
    }

    return false
  })
}

export const filterScenariosByTechCategories = (scenarios, search = []) => {
  if (search.length === 0) return scenarios
  return scenarios.filter((scenario) => {
    const techCategory = scenario?.multi_select_tags?.techCategory ?? []

    const matchesCategory = intersection(techCategory, search).length > 0
    if (matchesCategory) {
      return true
    }
    return false
  })
}

export const filterScenarios = (scenarios, search) => {
  const lowercaseSearch = search.toLowerCase().trim()
  return scenarios.filter((scenario) => {
    const matchesName = String(scenario.name)
      .toLowerCase()
      .includes(lowercaseSearch)
    if (matchesName) {
      return true
    }

    const command =
      Object.values(scenario.graph?.nodes ?? {}).map((node) =>
        node?.impact_definition?.infra_command_type?.toLowerCase()
      ) ?? []

    const matchesCommand = command.includes(lowercaseSearch)
    if (matchesCommand) {
      return true
    }

    const matchesAuthor = String(scenario.created_by)
      .toLowerCase()
      .includes(lowercaseSearch)
    if (matchesAuthor) {
      return true
    }

    const matchesDescription = String(scenario.description)
      .toLowerCase()
      .includes(lowercaseSearch)
    if (matchesDescription) {
      return true
    }

    return false
  })
}

export const ACTIVE_RUN_STAGES = ['Setup', 'Active']
export const ERROR_RUN_STAGES = ['SetupFailed', 'Failed']
export const COMPLETED_RUN_STAGES = ['Halted', 'Completed']

export const getRunStage = (run) => run?.stage_info?.stage

export const isRunInActiveStage = (run) =>
  ACTIVE_RUN_STAGES.includes(getRunStage(run))

export const isRunInCompletedStage = (run) =>
  isRunInErrorStage(run) || COMPLETED_RUN_STAGES.includes(getRunStage(run))

export const isRunInErrorStage = (run) =>
  ERROR_RUN_STAGES.includes(getRunStage(run))

export const isRunHalting = (run) => getRunStage(run) === 'Halted'

export const hasBeenRun = (scenario) => Boolean(scenario.last_run_at)

export const getRunStageMetadata = (run) => run?.stage_info?.stageMetadata

export const getTargetingStrategy = (targetType, target) => {
  const targetStrategy = {
    attrs: {
      //tags and labels go here
    }
  }
  if (target.targetAll) {
    if (targetType === 'Hosts') {
      targetStrategy.all_hosts = true
    } else if (targetType === 'Containers') {
      targetStrategy.all_containers = true
    }
  } else if (!isTargetParamsEmpty(target.targetParams)) {
    // If target params are not empty, add them using appropriate key
    targetStrategy.attrs[
      getTargetParamsKey(targetType, target.targetingStrategy)
    ] = target.targetParams
  }

  if (target.targetingStrategy === 'Random') {
    if (target.percentage) {
      targetStrategy.type = 'RandomPercent'
      targetStrategy.percentage = target.percentage
    } else {
      targetStrategy.type = 'RandomCount'
      targetStrategy.count = target.exact
    }
  } else {
    targetStrategy.type = 'Exact'
  }

  return targetStrategy
}

const getTargetType = (targetType) => {
  if (targetType === 'Hosts') {
    return 'Host'
  }
  if (targetType === 'Containers') {
    return 'Container'
  }
  return targetType
}

export const createAttackFromForm = (
  targetType,
  { target, impact },
  targets
) => {
  const isKubernetes = targetType === 'Kubernetes'

  const command = {
    cliArgs: [
      impact.type,
      ...(impact?.args?.map((arg) => arg.toString()) ?? [])
    ],
    providers: impact.providers,
    trafficImpactMapping: impact.trafficImpactMapping
      ? cleanObjKeysAndValues(impact.trafficImpactMapping, {
          keyTransformers: [snakeCase]
        })
      : undefined
  }

  const attack = {
    target_definition: {
      target_type: getTargetType(targetType),
      strategy_type: target.targetingStrategy,
      strategy: getTargetingStrategy(targetType, target)
    },
    impact_definition: {
      infra_command_type: impact.type,
      infra_command_args: {
        ...cleanObjKeysAndValues(command, {
          keysToRemove: ['commandType'],
          keyTransformers: [snakeCase]
        }),
        type: impact.type
      }
    },
    type: scenarioGraphTypes.InfraAttack
  }

  if (isKubernetes) {
    attack.target_definition.k8s_objects = getSelectedKubernetesTargets(
      targets,
      target
    ).map((k8sTarget) =>
      cleanObjKeysAndValues(k8sTarget, {
        keyTransformers: [snakeCase]
      })
    )
    if (impact.containerSelection) {
      attack.target_definition.containerSelection = {
        selectionType: impact.containerSelection.selectionType.toUpperCase(),
        containerNames: [...impact.containerSelection.containerNames]
      }
    }
  }
  return attack
}
